import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Home, Bug, ShieldAlert } from 'lucide-react';
import { logger } from '../../utils/logger';
import { cn } from '../../lib/utils';
import { Button } from '../ui/Button';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  onRetry?: () => void;
  maxRetries?: number;
  variant?: 'default' | 'dashboard' | 'minimal';
  showDetails?: boolean;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  retryCount: number;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: 0
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return {
      hasError: true,
      error
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({
      error,
      errorInfo
    });

    // Log error for debugging
    logger.error('ErrorBoundary caught an error:', {
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      timestamp: new Date().toISOString()
    });

    // Call optional error handler
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  handleRetry = () => {
    const maxRetries = this.props.maxRetries ?? 3;
    if (this.state.retryCount < maxRetries) {
      this.setState(prevState => ({
        hasError: false,
        error: null,
        errorInfo: null,
        retryCount: prevState.retryCount + 1
      }));
      
      if (this.props.onRetry) {
        this.props.onRetry();
      }
    }
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const { variant = 'default', showDetails = process.env.NODE_ENV === 'development' } = this.props;
      const maxRetries = this.props.maxRetries ?? 3;
      const canRetry = this.state.retryCount < maxRetries;

      if (variant === 'minimal') {
        return (
          <div className="p-4 rounded-lg bg-red-50 border border-red-100 flex flex-col items-center gap-3">
            <ShieldAlert className="w-8 h-8 text-red-500" />
            <p className="text-sm font-medium text-red-900 text-center">
              Une erreur est survenue dans ce composant.
            </p>
            <Button size="sm" variant="outline" onClick={this.handleRetry}>
              Réessayer
            </Button>
          </div>
        );
      }

      return (
        <div className={cn(
          "min-h-screen flex items-center justify-center p-4",
          variant === 'dashboard' ? "bg-gradient-to-br from-slate-50 to-slate-100" : "bg-slate-50"
        )}>
          <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center border border-slate-100">
            <div className="flex justify-center mb-6">
              <div className="p-4 bg-red-100 rounded-2xl">
                <AlertTriangle className="w-10 h-10 text-red-600" />
              </div>
            </div>

            <h1 className="text-2xl font-bold text-slate-900 mb-2">
              Une erreur est survenue
            </h1>

            <p className="text-slate-600 mb-8">
              {this.state.error?.message || 'Une erreur inattendue s\'est produite. Nos équipes ont été notifiées.'}
            </p>

            <div className="space-y-3">
              {canRetry && (
                <Button
                  onClick={this.handleRetry}
                  className="w-full gap-2"
                >
                  <RefreshCw size={18} className={cn(this.state.retryCount > 0 && "animate-spin")} />
                  Réessayer {maxRetries > 0 && `(${maxRetries - this.state.retryCount} restantes)`}
                </Button>
              )}

              <div className="grid grid-cols-2 gap-3">
                <Button
                  variant="outline"
                  onClick={this.handleGoHome}
                  className="gap-2"
                >
                  <Home size={18} />
                  Accueil
                </Button>

                <Button
                  variant="ghost"
                  onClick={this.handleReload}
                  className="gap-2"
                >
                  <RefreshCw size={18} />
                  Recharger
                </Button>
              </div>
            </div>

            {showDetails && this.state.error && (
              <details className="mt-8 text-left group">
                <summary className="cursor-pointer text-xs text-slate-400 hover:text-slate-600 flex items-center gap-1 transition-colors">
                  <Bug size={14} />
                  Détails techniques
                </summary>
                <div className="mt-3 p-4 bg-slate-50 rounded-xl text-[10px] font-mono text-slate-500 overflow-auto max-h-48 border border-slate-100">
                  <div className="font-bold text-slate-700 mb-1">Error:</div>
                  <div className="mb-3 break-words">{this.state.error.message}</div>
                  {this.state.error.stack && (
                    <>
                      <div className="font-bold text-slate-700 mb-1">Stack trace:</div>
                      <div className="whitespace-pre-wrap opacity-75">{this.state.error.stack}</div>
                    </>
                  )}
                </div>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Shortcut components
export const DashboardErrorBoundary: React.FC<{ children: ReactNode }> = ({ children }) => (
  <ErrorBoundary variant="dashboard">{children}</ErrorBoundary>
);

export const MinimalErrorBoundary: React.FC<{ children: ReactNode }> = ({ children }) => (
  <ErrorBoundary variant="minimal">{children}</ErrorBoundary>
);
